// Funnel: row per simulation step. Each bar's width is proportional to the
// teachers still in after the step vs the starting total. Connector polygons
// between rows show the failed cohort. Click handlers fan out into the modal
// flow.
import { useTranslation } from "react-i18next";

const getBarColor = (ratio) => {
  // Hue 0 (red) → 130 (green). 45% lightness keeps white text readable.
  const hue = Math.round(ratio * 130);
  return `hsl(${hue}, 75%, 45%)`;
};

export default function FunnelChart({ data, onSegmentClick }) {
  const { t } = useTranslation();
  // Aggregated scopes pass no handler — the counts have no teacher rows behind
  // them — so the pointer and the hover emphasis stay off (same guard as
  // BottleneckHeatmap).
  const canDrill = Boolean(onSegmentClick);
  const totalStart = data.length > 0 ? data[0].inputCount : 0;
  const maxValue = totalStart > 0 ? totalStart : 1;

  const rows = [
    {
      id: "start",
      label: t("standards.totalTeachersEvaluated"),
      value: totalStart,
      originalIndex: -1,
    },
    ...data.map((step) => ({
      id: step.stepIndex,
      label: step.label,
      value: step.carriedCount,
      originalIndex: step.stepIndex,
    })),
  ];

  return (
    <div className="w-full h-full flex flex-col justify-center py-2">
      {rows.map((row, index) => {
        const nextRow = rows[index + 1];

        const ratio = maxValue > 0 ? row.value / maxValue : 0;
        const currentWidthPct = ratio * 100;
        const nextRatio = nextRow && maxValue > 0 ? nextRow.value / maxValue : 0;
        const nextWidthPct = nextRatio * 100;

        const barColor = getBarColor(ratio);

        const x1Left = (100 - currentWidthPct) / 2;
        const x1Right = 100 - x1Left;
        const x2Left = (100 - nextWidthPct) / 2;
        const x2Right = 100 - x2Left;

        return (
          <div key={row.id} className="flex flex-col w-full">
            <div className="flex items-center w-full min-h-[2.5rem] sm:min-h-[3rem]">
              <div className="w-32 sm:w-64 pr-2 sm:pr-4 text-right text-xs sm:text-sm text-slate-500 font-medium shrink-0 flex flex-col justify-center h-full leading-tight">
                {row.label}
              </div>
              <div
                className={`flex-1 h-full flex justify-center px-4 relative group ${
                  canDrill ? "cursor-pointer" : ""
                }`}
                onClick={() =>
                  onSegmentClick?.(
                    row.originalIndex === -1 ? "total" : "pass",
                    row.originalIndex,
                  )
                }
              >
                <div
                  className={`h-full flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-sm transition-all duration-500 z-10 min-w-[2rem] whitespace-nowrap overflow-hidden ${
                    canDrill ? "group-hover:scale-105" : ""
                  }`}
                  style={{
                    width: `${currentWidthPct}%`,
                    backgroundColor: barColor,
                    textShadow: "0px 1px 2px rgba(0,0,0,0.2)",
                  }}
                >
                  {row.value}
                </div>
              </div>
            </div>

            {nextRow && (
              <div className="flex w-full h-4 sm:h-6">
                <div className="w-32 sm:w-64 shrink-0" />
                <div
                  className={`flex-1 px-4 relative group ${
                    canDrill ? "cursor-pointer" : ""
                  }`}
                  onClick={() => onSegmentClick?.("fail", nextRow.originalIndex)}
                >
                  <svg
                    className="w-full h-full block"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <polygon
                      points={`${x1Left},0 ${x1Right},0 ${x2Right},100 ${x2Left},100`}
                      fill="#cbd5e1"
                      className={`opacity-60 transition-all ${
                        canDrill
                          ? "group-hover:opacity-100 group-hover:fill-rose-300"
                          : ""
                      }`}
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
